<script lang="ts">
  /**
   * ⌘K / Ctrl+K global command palette. Modal centered overlay with
   * a single large search input and keyboard-navigable result groups.
   */
  import { onMount } from 'svelte';
  import { goto } from '$app/navigation';
  import SearchIcon from '$components/ui/icons/SearchIcon.svelte';
  import { SUBJECTS } from './subjects';

  interface CommandItem {
    id: string;
    label: string;
    href: string;
    shortcut?: string;
  }
  interface CommandGroup {
    id: string;
    title: string;
    items: CommandItem[];
  }

  const GO_ITEMS: CommandItem[] = [
    { id: 'home',    label: 'Басты бет',   href: '/' },
    { id: 'search',  label: 'Іздеу',       href: '/search' },
    { id: 'learn',   label: 'Оқу',         href: '/learn' },
    { id: 'test',    label: 'Тест',        href: '/test' },
    { id: 'about',   label: 'Біз туралы',  href: '/about' },
    { id: 'profile', label: 'Профиль',     href: '/profile' }
  ];

  let open = $state(false);
  let query = $state('');
  let activeIndex = $state(0);
  let inputEl = $state<HTMLInputElement | null>(null);

  const subjectItems: CommandItem[] = SUBJECTS.map((s) => ({
    id: `subject-${s.id}`,
    label: s.label,
    href: `/search?subject=${s.id}`
  }));

  const groups = $derived<CommandGroup[]>(buildGroups(query));

  function buildGroups(q: string): CommandGroup[] {
    const qLower = q.trim().toLowerCase();
    const filter = (items: CommandItem[]): CommandItem[] =>
      qLower === ''
        ? items
        : items.filter((i) => i.label.toLowerCase().includes(qLower));

    const result: CommandGroup[] = [];
    const go = filter(GO_ITEMS);
    if (go.length) result.push({ id: 'go', title: 'ӨТУ', items: go });

    if (qLower !== '') {
      result.push({
        id: 'search',
        title: 'ІЗДЕУ',
        items: [
          {
            id: 'search-query',
            label: `"${q}" — кітаптардан іздеу`,
            href: `/search?q=${encodeURIComponent(q)}`
          }
        ]
      });
    }

    const subjects = filter(subjectItems);
    if (subjects.length) result.push({ id: 'subjects', title: 'ПӘНДЕР', items: subjects });

    return result;
  }

  // Flattened list for arrow-key navigation.
  const flat = $derived<CommandItem[]>(groups.flatMap((g) => g.items));

  $effect(() => {
    if (activeIndex >= flat.length) activeIndex = 0;
  });

  function openPalette(): void {
    open = true;
    query = '';
    activeIndex = 0;
    queueMicrotask(() => inputEl?.focus());
  }
  function closePalette(): void {
    open = false;
  }

  function select(item: CommandItem): void {
    closePalette();
    // External? open in new tab. Otherwise SPA navigation.
    if (/^https?:/.test(item.href)) {
      window.open(item.href, '_blank', 'noopener');
    } else {
      goto(item.href);
    }
  }

  function onKeyDown(e: KeyboardEvent): void {
    const isMod = e.metaKey || e.ctrlKey;
    if (isMod && (e.key === 'k' || e.key === 'K')) {
      e.preventDefault();
      open ? closePalette() : openPalette();
      return;
    }
    if (!open) return;
    if (e.key === 'Escape') { e.preventDefault(); closePalette(); return; }
    if (e.key === 'ArrowDown') { e.preventDefault(); activeIndex = Math.min(activeIndex + 1, flat.length - 1); }
    else if (e.key === 'ArrowUp') { e.preventDefault(); activeIndex = Math.max(activeIndex - 1, 0); }
    else if (e.key === 'Enter') {
      e.preventDefault();
      const item = flat[activeIndex];
      if (item) select(item);
    }
  }

  onMount(() => {
    const handleExternal = (): void => openPalette();
    window.addEventListener('open-command-palette', handleExternal);
    return () => window.removeEventListener('open-command-palette', handleExternal);
  });

  // Compute running index across groups for aria/keyboard state.
  function indexOf(groupIdx: number, itemIdx: number): number {
    let n = 0;
    for (let g = 0; g < groupIdx; g++) n += groups[g]!.items.length;
    return n + itemIdx;
  }
</script>

<svelte:window onkeydown={onKeyDown} />

{#if open}
  <div
    class="backdrop"
    onclick={closePalette}
    role="presentation"
    aria-hidden="true"
  ></div>
  <div
    class="palette"
    role="dialog"
    aria-modal="true"
    aria-label="Командалық панель"
  >
    <div class="search-row">
      <SearchIcon size={18} />
      <input
        bind:this={inputEl}
        bind:value={query}
        class="search-input"
        placeholder="Нені іздейсіз?"
        autocomplete="off"
        spellcheck="false"
      />
      <span class="esc-hint">Esc</span>
    </div>

    <div class="results" role="listbox">
      {#each groups as group (group.id)}
        <div class="group">
          <div class="group-title">{group.title}</div>
          {#each group.items as item, j (item.id)}
            {@const idx = indexOf(groups.indexOf(group), j)}
            <button
              class="item"
              class:active={idx === activeIndex}
              onmouseenter={() => (activeIndex = idx)}
              onclick={() => select(item)}
              role="option"
              aria-selected={idx === activeIndex}
              type="button"
            >
              <span class="item-label">{item.label}</span>
              {#if item.shortcut}
                <kbd class="item-kbd">{item.shortcut}</kbd>
              {/if}
            </button>
          {/each}
        </div>
      {/each}
      {#if flat.length === 0}
        <div class="empty">Нәтиже табылмады</div>
      {/if}
    </div>
  </div>
{/if}

<style>
  .backdrop {
    position: fixed;
    inset: 0;
    z-index: var(--z-overlay);
    background: rgba(0, 0, 0, 0.4);
    animation: fadeIn var(--dur-fast) var(--ease);
  }
  .palette {
    position: fixed;
    top: 15vh;
    left: 50%;
    transform: translateX(-50%);
    z-index: var(--z-modal);
    width: min(560px, calc(100vw - 2 * var(--space-4)));
    max-height: 70vh;
    background: var(--color-bg-elevated);
    border: 1px solid var(--color-border);
    border-radius: var(--radius-2xl);
    box-shadow: var(--shadow-xl);
    display: flex;
    flex-direction: column;
    overflow: hidden;
    animation: palettePop var(--dur-slow) var(--ease-spring);
  }
  .search-row {
    display: flex;
    align-items: center;
    gap: var(--space-3);
    padding: var(--space-4);
    border-bottom: 1px solid var(--color-border-subtle);
    color: var(--color-fg-muted);
  }
  .search-input {
    flex: 1;
    border: 0;
    background: transparent;
    outline: none;
    font-size: var(--text-lg);
    font-family: inherit;
    color: var(--color-fg);
  }
  .search-input::placeholder { color: var(--color-fg-muted); }
  .esc-hint {
    font-family: var(--font-mono);
    font-size: var(--text-xs);
    padding: 2px var(--space-2);
    border: 1px solid var(--color-border);
    border-radius: var(--radius-xs);
    color: var(--color-fg-secondary);
  }

  .results {
    overflow-y: auto;
    padding: var(--space-2);
  }
  .group { margin-bottom: var(--space-2); }
  .group-title {
    font-size: var(--text-xs);
    font-weight: var(--fw-medium);
    letter-spacing: 0.06em;
    color: var(--color-fg-muted);
    padding: var(--space-2) var(--space-3);
  }
  .item {
    display: flex;
    align-items: center;
    justify-content: space-between;
    width: 100%;
    padding: var(--space-2) var(--space-3);
    border: 0;
    background: transparent;
    color: var(--color-fg);
    font-size: var(--text-sm);
    font-family: inherit;
    border-radius: var(--radius-md);
    cursor: pointer;
    text-align: left;
    transition: background-color var(--dur-fast) var(--ease);
  }
  .item.active { background: var(--color-bg-muted); }
  .item-label { min-width: 0; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
  .item-kbd {
    font-family: var(--font-mono);
    font-size: var(--text-xs);
    color: var(--color-fg-muted);
  }
  .empty {
    padding: var(--space-8);
    text-align: center;
    color: var(--color-fg-muted);
    font-size: var(--text-sm);
  }

  @keyframes fadeIn { from { opacity: 0; } to { opacity: 1; } }
  @keyframes palettePop {
    from { opacity: 0; transform: translate(-50%, -4px); }
    to   { opacity: 1; transform: translate(-50%, 0); }
  }
  @media (prefers-reduced-motion: reduce) {
    .backdrop, .palette { animation: none !important; }
  }
</style>
